import React, {useCallback, useState} from 'react';
import {
    Eyebrow,
    Typography as Text,
    VerticalSpacer,
    SubnavItemNumber,
    GridContainer,
    SectionInnerGridContentBreakout
} from '../../elements';
import styled from 'styled-components';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {PersonProps, Person} from './Person';
import {BioModal} from './BioModal';
import {AccentColorOptions} from 'epic-ue-theme';
import {InView} from 'react-intersection-observer';
import Grid from '@material-ui/core/Grid';
import root from 'window-or-global';

const PeopleContainer = styled.div`
    position: relative;
    overflow: hidden;
`;

const PersonsWrapper = styled.div`
    &.modal-open {
        opacity: 0.7;
    }

    .people-intro {
        ${(p) => p.theme.breakpoints.down('sm')} {
            text-align: center;
        }
        .paragraph {
            color: ${(p) => p.theme.palette.text.secondary};
        }
    }
`;

export interface PeopleProps {
    persons?: PersonProps[];
    eyebrow?: {
        index?: number;
        text?: string;
    };
    title?: string;
    description?: string;
    accentColor?: AccentColorOptions;
}

export const People = ({
    persons = [],
    eyebrow,
    title,
    description,
    accentColor = 'sky'
}: PeopleProps): JSX.Element => {
    const [inView, setInView] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalPersonIndex, setModalPersonIndex] = useState(0);

    const visChange = useCallback(
        (visible) => {
            setInView(visible);
        },
        [setInView]
    );

    const toggleBio = useCallback(
        (index) => {
            root.document.getElementsByTagName('body')[0].style.overflow = 'initial';
            root.document.getElementsByTagName('body')[0].style.position = 'initial';
            root.document.getElementsByTagName('body')[0].style.height = 'initial';
            if (inView) {
                if (index === null) {
                    setShowModal(false);
                } else {
                    setShowModal(true);
                    setModalPersonIndex(index);
                    //Needed to prevent background scrolling
                    root.document.getElementsByTagName('body')[0].style.overflow = 'hidden';

                    //Needed to prevent background scrolling on iphones
                    root.document.getElementsByTagName('body')[0].style.height = '100vh';
                    root.document.getElementsByTagName('body')[0].style.position = 'relative';
                }
            } else {
                setShowModal(false);
                setModalPersonIndex(0);
            }
        },
        [inView, setShowModal, setModalPersonIndex]
    );

    return (
        <>
            <BioModal
                {...persons[modalPersonIndex]}
                isOpen={showModal}
                onClick={toggleBio}
                classes="bio-modal"
            />
            <InView as="div" onChange={visChange} threshold={0}>
                <PeopleContainer>
                    <PersonsWrapper className={`${showModal ? 'modal-open' : ''}`}>
                        <GridContainer>
                            <SectionInnerGridContentBreakout>
                                <div className="people-intro">
                                    <AnimateOnViewWrap translateY={30}>
                                        {eyebrow && eyebrow.text ? (
                                            <>
                                                <Eyebrow>
                                                    {eyebrow.index && (
                                                        <SubnavItemNumber
                                                            big
                                                            index={eyebrow.index}
                                                        />
                                                    )}

                                                    <Text
                                                        variant="h4"
                                                        big
                                                        color={
                                                            eyebrow.index ? accentColor : undefined
                                                        }>
                                                        {eyebrow.text}
                                                    </Text>
                                                </Eyebrow>
                                                <VerticalSpacer variant="heading" />
                                            </>
                                        ) : (
                                            ''
                                        )}
                                        {title && (
                                            <Text variant="h2" big>
                                                {title}
                                            </Text>
                                            
                                           
                                        )}
                                        

                                        {description && (
                                            <>
                                            <VerticalSpacer variant="heading" />
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center">
                                                <Grid item xs={12} md={8}>
                                                    <Text variant="p">
                                                        <span
                                                            className="people-desc"
                                                            dangerouslySetInnerHTML={{
                                                                __html: description
                                                            }}
                                                        />
                                                    </Text>
                                                </Grid>
                                            </Grid>
                                            </>
                                        )}
                                    </AnimateOnViewWrap>
                                </div>
                            </SectionInnerGridContentBreakout>

                            <VerticalSpacer variant="custom" spacing={7} />
                            <Grid
                                container
                                direction="row"
                                justifyContent="center"
                                alignItems="flex-start"
                                spacing={6}>
                                {persons.map((person, i) => (
                                    <Grid
                                        key={`f-person-${person.name}-${i}`}
                                        item
                                        xs={6}
                                        md={4}
                                        lg={3}>
                                        <Person
                                            index={i}
                                            {...person}
                                            accentColor={accentColor}
                                            onClick={toggleBio}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </GridContainer>
                    </PersonsWrapper>
                </PeopleContainer>
            </InView>
        </>
    );
};
